import type { CommentThread } from "@/api";
import { useTaskList } from "@/models/richContent/taskListLifecycle";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as PageOptions from "@/components/PaperContainer/PageOptions";
import { DiscussionReactions } from "./DiscussionReactions";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";

import { Comments } from "./Comments";

import { useReadNotificationsOnLoad } from "@/models/notifications/notificationLifecycle";
import { invalidateProjectInteractionQueries } from "@/models/projects/projectInteractionQueries";
import { Avatar, IconEdit, CurrentSubscriptions, RichContent, FormattedTime } from "turboui";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";

import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { usePaths } from "../../routes/paths";
import { useCurrentSubscriptionsQueryAdapter } from "@/models/subscriptions/useCurrentSubscriptionsQueryAdapter";
import { useLoadedData, useRefresh } from "./loader";

export function Page() {
  const { t } = useTranslation();
  const { discussion } = useLoadedData();

  const project = discussion.project;
  useReadNotificationsOnLoad(
    discussion.notifications ?? [],
    project
      ? (client) =>
          invalidateProjectInteractionQueries(
            client,
            {
              projectId: project.id,
              spaceId: discussion.space?.id,
              resourceId: discussion.id,
              resourceType: "project_discussion",
            },
            "none",
          )
      : undefined,
  );

  return (
    <Pages.Page title={[discussion.title || t("Discussion"), discussion.project?.name || ""]}>
      <Paper.Root>
        <Nav />

        <Paper.Body>
          <Options />
          <Title />
          <Content />
          <DiscussionReactions />
          <Comments />
          <Subscriptions />
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function Options() {
  const { t } = useTranslation();
  const { discussion } = useLoadedData();
  const paths = usePaths();

  return (
    <PageOptions.Root testId="options">
      {canEditDiscussion(discussion) && (
        <PageOptions.Link
          icon={IconEdit}
          title={t("Edit")}
          to={paths.projectDiscussionEditPath(discussion.id)}
          testId="edit"
          keepOutsideOnBigScreen
        />
      )}
    </PageOptions.Root>
  );
}

function Content() {
  const { discussion } = useLoadedData();
  const message = JSON.parse(discussion.message || "{}");
  const { mentionedPersonLookup } = useRichEditorHandlers();
  const taskList = useTaskList({
    resourceType: "project_discussion",
    resourceId: discussion.id,
    field: "message",
    canEdit: canEditDiscussion(discussion),
  });

  return (
    <div className="my-8">
      <RichContent taskList={taskList} content={message} mentionedPersonLookup={mentionedPersonLookup} />
    </div>
  );
}

function Nav() {
  const { t } = useTranslation();
  const paths = usePaths();
  const { discussion } = useLoadedData();

  const items: Paper.NavigationItem[] = [];

  if (discussion.space) {
    items.push({ to: paths.spacePath(discussion.space.id), label: discussion.space.name });
    items.push({ to: paths.spaceWorkMapPath(discussion.space.id, "projects"), label: t("Work Map") });
  } else {
    items.push({ to: paths.workMapPath("projects"), label: t("Work Map") });
  }

  if (discussion.project) {
    items.push({ to: paths.projectPath(discussion.project.id, { tab: "overview" }), label: discussion.project.name });
    items.push({ to: paths.projectPath(discussion.project.id, { tab: "discussions" }), label: t("Discussions") });
  }

  return <Paper.Navigation items={items} />;
}

function Title() {
  const { discussion } = useLoadedData();
  const formattedTimePreferences = useFormattedTimePreferences();

  return (
    <div className="flex items-center gap-3">
      {discussion.author && <Avatar person={discussion.author} size={50} />}
      <div>
        <div className="text-content-accent text-2xl font-bold leading-tight">{discussion.title}</div>
        <div className="inline-flex items-center gap-1">
          <Trans
            i18nKey="<author>{{name}}</author> on <date/>"
            values={{ name: discussion.author?.fullName ?? "" }}
            components={{
              author: <span />,
              date: <FormattedTime {...formattedTimePreferences} time={discussion.insertedAt} format="long-date" />,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function Subscriptions() {
  const refresh = useRefresh();
  const { discussion, isCurrentUserSubscribed } = useLoadedData();

  const subscriptionsState = useCurrentSubscriptionsQueryAdapter({
    potentialSubscribers: discussion.potentialSubscribers ?? [],
    subscriptionList: discussion.subscriptionList,
    resourceName: "discussion",
    type: "comment_thread",
    onRefresh: refresh,
  });

  if (!discussion.potentialSubscribers || !discussion.subscriptionList) {
    return null;
  }

  return (
    <div className="border-t border-stroke-base mt-16 pt-8">
      <CurrentSubscriptions
        {...subscriptionsState}
        isCurrentUserSubscribed={isCurrentUserSubscribed}
        canEditSubscribers={discussion.projectPermissions?.canEdit || false}
      />
    </div>
  );
}

function canEditDiscussion(discussion: CommentThread): boolean {
  return discussion.projectPermissions?.canEdit ?? false;
}
